import { useMemo, useState } from 'react'
import type { AgentOutputProps } from '../types'
import TranscriptRenderer from './TranscriptRenderer'
import ActionItemsRenderer from './ActionItemsRenderer'

/**
 * MeetingTranscriptRenderer — composite view for the meeting-notes crew
 * result (transcript + summary + topics + decisions + action_items).
 *
 * Shows a tab selector over the four sub-views. Each sub-view reuses an
 * existing renderer (TranscriptRenderer, ActionItemsRenderer) or inlines
 * a small layout for summary / topics / decisions.
 */

type Tab = 'summary' | 'transcript' | 'actions' | 'topics'

interface Topic {
  topic: string
  summary?: string
  duration_seconds?: number
}

interface Decision {
  decision: string
  context?: string
  decided_by?: string
}

interface MeetingPackage {
  summary?: string
  topics?: Topic[]
  decisions?: Decision[]
  key_points?: string[]
  transcript_text?: string
  segments?: any[]
  action_items?: any[]
  speakers?: string[]
  duration_seconds?: number
  stt_provider?: string
  stt_cost?: number
}

function pick(result: any): MeetingPackage | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (src && (src.summary || src.transcript_text || Array.isArray(src.action_items))) {
      return src as MeetingPackage
    }
  }
  return null
}

function fmtSec(s?: number): string {
  if (typeof s !== 'number') return ''
  const m = Math.floor(s / 60)
  return `${m}m`
}

export default function MeetingTranscriptRenderer(props: AgentOutputProps) {
  const pkg = useMemo(() => pick(props.result), [props.result])
  const [tab, setTab] = useState<Tab>('summary')

  if (!pkg) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No meeting data found in the agent result.
      </div>
    )
  }

  const tabs: { key: Tab; label: string; count?: number; visible: boolean }[] = [
    { key: 'summary', label: 'Summary', visible: !!pkg.summary || !!pkg.key_points?.length },
    {
      key: 'transcript',
      label: 'Transcript',
      visible: !!pkg.transcript_text || !!pkg.segments?.length,
    },
    { key: 'actions', label: 'Action Items', count: pkg.action_items?.length, visible: !!pkg.action_items?.length },
    { key: 'topics', label: 'Topics & Decisions', count: (pkg.topics?.length || 0) + (pkg.decisions?.length || 0), visible: !!pkg.topics?.length || !!pkg.decisions?.length },
  ]
  const activeTabs = tabs.filter((t) => t.visible)
  const active = activeTabs.some((t) => t.key === tab) ? tab : activeTabs[0]?.key

  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {activeTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              t.key === active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            {typeof t.count === 'number' && (
              <span className="ml-2 text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Panel */}
      {active === 'summary' && (
        <div className="space-y-3">
          {pkg.summary && (
            <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {pkg.summary}
            </div>
          )}
          {pkg.key_points && pkg.key_points.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">
                Key Points
              </div>
              <ul className="space-y-1 text-sm text-foreground">
                {pkg.key_points.map((p, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-primary">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {active === 'transcript' && <TranscriptRenderer {...props} />}

      {active === 'actions' && <ActionItemsRenderer {...props} />}

      {active === 'topics' && (
        <div className="space-y-4">
          {pkg.topics && pkg.topics.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                Topics Discussed
              </div>
              <div className="space-y-2">
                {pkg.topics.map((t, i) => (
                  <div key={i} className="border border-border rounded-md p-3 bg-muted/20">
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-sm font-semibold text-foreground">{t.topic}</div>
                      {t.duration_seconds && (
                        <div className="text-xs text-muted-foreground font-mono">{fmtSec(t.duration_seconds)}</div>
                      )}
                    </div>
                    {t.summary && <div className="text-sm text-muted-foreground">{t.summary}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {pkg.decisions && pkg.decisions.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                Decisions
              </div>
              <div className="space-y-2">
                {pkg.decisions.map((d, i) => (
                  <div
                    key={i}
                    className="border border-border rounded-md p-3"
                    style={{ borderLeft: '4px solid #10B981' }}
                  >
                    <div className="text-sm font-semibold text-foreground mb-1">{d.decision}</div>
                    {d.context && <div className="text-xs text-muted-foreground mb-1">{d.context}</div>}
                    {d.decided_by && (
                      <div className="text-xs text-muted-foreground italic">— {d.decided_by}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
